import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  GraduationCap,
  Landmark,
  UtensilsCrossed,
  Briefcase,
  Store,
  Cog,
  Home,
  Snowflake,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  Facebook,
  ExternalLink,
  Eye,
} from "lucide-react";
import { api, formatApiErrorDetail, fileUrl, LOGO_URL, trackConversion } from "../../lib/api";
import { usePrefs } from "../../context/PrefsContext";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const POPUP_DISMISS_KEY = "tmg_popup_dismissed_v1";

export const PromoPopup = ({ popup }) => {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!popup || !popup.enabled) return;
    if (!popup.media_url && !popup.title && !popup.subtitle) return;
    if (popup.show_once) {
      try {
        if (sessionStorage.getItem(POPUP_DISMISS_KEY) === "1") return;
      } catch (e) { /* storage unavailable, still show */ }
    }
    const timer = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(timer);
  }, [popup]);

  const dismiss = () => {
    try { sessionStorage.setItem(POPUP_DISMISS_KEY, "1"); } catch (e) { /* noop */ }
    setOpen(false);
  };

  if (!popup) return null;
  const isVideo = popup.media_type === "video" || (popup.media_url || "").match(/\.(mp4|webm|mov)$/i);
  const hasText = !!(popup.title || popup.subtitle || (popup.cta_text && popup.cta_link));
  const mediaOnly = !!popup.media_url && !hasText;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent
        data-testid="promo-popup"
        aria-describedby={undefined}
        className={`bg-white dark:bg-slate-900 border-0 p-0 overflow-hidden rounded-2xl shadow-2xl ${mediaOnly ? "max-w-2xl" : "max-w-3xl"}`}
      >
        {(mediaOnly || !popup.title) && <DialogTitle className="sr-only">Promosi Tanjung Marina Global</DialogTitle>}
        {mediaOnly ? (
          <div className="relative bg-black flex items-center justify-center">
            {isVideo ? (
              <video
                data-testid="promo-popup-video"
                src={fileUrl(popup.media_url)}
                className="w-full h-auto max-h-[80vh] object-contain"
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                data-testid="promo-popup-image"
                src={fileUrl(popup.media_url)}
                alt={popup.title || "Promo"}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2">
            {popup.media_url ? (
              <div className="relative bg-slate-900 aspect-square md:aspect-auto md:min-h-[420px] flex items-center justify-center overflow-hidden">
                {isVideo ? (
                  <video
                    data-testid="promo-popup-video"
                    src={fileUrl(popup.media_url)}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    data-testid="promo-popup-image"
                    src={fileUrl(popup.media_url)}
                    alt={popup.title || "Promo"}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none md:hidden" />
              </div>
            ) : (
              <div className="bg-gradient-to-br from-orange-500 to-orange-700 min-h-[220px] md:min-h-[420px] flex items-center justify-center">
                <span className="font-heading font-black text-white/25 text-8xl">TMG</span>
              </div>
            )}
            <div className="p-8 md:p-10 flex flex-col justify-center bg-white dark:bg-slate-900">
              {popup.title && (
                <DialogTitle className="font-heading font-extrabold text-slate-900 dark:text-white text-2xl md:text-3xl leading-tight mb-4">
                  {popup.title}
                </DialogTitle>
              )}
              {popup.subtitle && (
                <p className="text-slate-600 dark:text-slate-300 text-sm md:text-base leading-relaxed whitespace-pre-line">
                  {popup.subtitle}
                </p>
              )}
              {popup.cta_text && popup.cta_link && (
                <a
                  href={popup.cta_link}
                  data-testid="promo-popup-cta"
                  target={popup.cta_link.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  onClick={dismiss}
                  className="mt-6 inline-flex items-center justify-center gap-2 self-start bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-orange-600/25 transition-all hover:-translate-y-0.5"
                >
                  {popup.cta_text}
                </a>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const WhyUs = ({ values }) => {
  const { t } = usePrefs();
  return (
    <section data-testid="values-section" className="py-24 lg:py-32 bg-white dark:bg-[#0b1533]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.values.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.values.heading}</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <motion.div
              key={v.name}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="border border-slate-200 dark:border-white/10 rounded-xl p-8 bg-slate-50 dark:bg-slate-900/50 shadow-sm dark:shadow-none hover:border-orange-500/50 hover:-translate-y-1 transition-all"
            >
              <span className="font-heading text-orange-500/60 font-extrabold text-4xl block mb-4">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-lg mb-2">{v.name}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const INDUSTRY_ICONS = [GraduationCap, Landmark, UtensilsCrossed, Briefcase, Store, Cog, Home, Snowflake];

export const Industries = ({ industries }) => {
  const { t } = usePrefs();
  return (
    <section id="industri" data-testid="industries-section" className="py-24 lg:py-32 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.industries.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.industries.heading}</h2>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {industries.map((name, i) => {
            const Icon = INDUSTRY_ICONS[i % INDUSTRY_ICONS.length];
            return (
              <motion.div
                key={name}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-center gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl p-5 shadow-sm dark:shadow-none hover:border-orange-500/50 transition-colors"
              >
                <Icon className="text-orange-600 dark:text-orange-500 shrink-0" size={22} />
                <span className="text-slate-700 dark:text-slate-200 text-sm font-medium">{name}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Milestones = ({ milestones }) => {
  const { t } = usePrefs();
  return (
    <section data-testid="milestones-section" className="py-24 lg:py-32 bg-white dark:bg-[#0b1533]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.milestones.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.milestones.heading}</h2>
        </motion.div>
        <div className="border-l-2 border-slate-300 dark:border-slate-800 ml-3 space-y-12">
          {milestones.map((m, i) => (
            <motion.div key={m.year} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative pl-10">
              <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-orange-600 border-4 border-white dark:border-[#0b1533]" />
              <span className="font-heading font-extrabold text-orange-600 dark:text-orange-500 text-2xl">{m.year}</span>
              <p className="text-slate-700 dark:text-slate-300 mt-2">{m.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Team = ({ team }) => {
  const { t } = usePrefs();
  const [selected, setSelected] = useState(null);
  const PUBLIC_FIELDS = [
    ["position", "position"],
    ["gender", "gender"],
    ["age", "age"],
    ["status", "status"],
    ["nationality", "nationality"],
    ["race", "race"],
    ["religion", "religion"],
    ["education", "education"],
    ["institution", "institution"],
  ];
  const hasAnyBio = (b) => b && (b.summary || PUBLIC_FIELDS.some(([k]) => (b[k] || "").toString().trim()));
  return (
    <section data-testid="team-section" className="py-24 lg:py-32 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.team.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.team.heading}</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {team.map((tm, i) => (
            <motion.div
              key={tm.name + i}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              onClick={() => setSelected(tm)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(tm); } }}
              data-testid={`team-card-${i}`}
              className="group text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl p-6 shadow-sm dark:shadow-none hover:border-orange-500/70 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer relative outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            >
              {tm.photo ? (
                <img
                  src={fileUrl(tm.photo)}
                  alt={tm.name}
                  className="w-20 h-20 rounded-full object-cover object-top border-2 border-orange-500/40 group-hover:border-orange-500 transition-colors mb-4"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-orange-600/15 border-2 border-orange-500/30 flex items-center justify-center mb-4">
                  <Users className="text-orange-600 dark:text-orange-500" size={26} />
                </div>
              )}
              <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{tm.name}</h3>
              <p className="text-orange-600 dark:text-orange-400/80 text-sm mt-1">{tm.role}</p>
              <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider text-orange-600/0 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors flex items-center gap-1">
                <Eye size={12} /> {t.bio.viewBio}
              </span>
              <p className="mt-3 text-[11px] text-slate-400 dark:text-slate-500 italic">{t.bio.clickHint} →</p>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent
          data-testid="team-bio-modal"
          aria-describedby={undefined}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 max-w-lg p-0 overflow-hidden"
        >
          {selected && (
            <div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-700 px-6 py-8 flex items-center gap-5">
                {selected.photo ? (
                  <img src={fileUrl(selected.photo)} alt={selected.name} className="w-24 h-24 rounded-full object-cover object-top border-4 border-white/40 shadow-xl" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-white/40 flex items-center justify-center">
                    <Users className="text-white" size={32} />
                  </div>
                )}
                <div>
                  <DialogTitle className="text-white font-heading text-2xl font-bold leading-tight">{selected.name}</DialogTitle>
                  <p className="text-white/90 text-sm mt-1">{selected.role}</p>
                </div>
              </div>
              <div className="px-6 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
                {selected.bio?.summary && (
                  <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed italic border-l-2 border-orange-500 pl-3">{selected.bio.summary}</p>
                )}
                {hasAnyBio(selected.bio) ? (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {PUBLIC_FIELDS.map(([key, labelKey]) => {
                      const val = (selected.bio?.[key] || "").toString().trim();
                      if (!val) return null;
                      const displayVal = key === "age" && /^\d+$/.test(val) ? `${val} ${t.bio.years}` : val;
                      return (
                        <div key={key} className="flex flex-col">
                          <dt className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">{t.bio[labelKey]}</dt>
                          <dd className="text-sm text-slate-800 dark:text-slate-200 font-medium mt-0.5">{displayVal}</dd>
                        </div>
                      );
                    })}
                  </dl>
                ) : (
                  <p className="text-slate-500 dark:text-slate-500 text-sm text-center italic">{t.bio.noBio}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const STATE_POS = {
  perlis: { x: 14, y: 4, side: "left" },
  kedah: { x: 22, y: 12 },
  "pulau pinang": { x: 11, y: 23, side: "left" },
  kelantan: { x: 51, y: 15 },
  terengganu: { x: 68, y: 26 },
  perak: { x: 27, y: 34, side: "left" },
  pahang: { x: 55, y: 47 },
  selangor: { x: 30, y: 62, side: "left" },
  "kuala lumpur": { x: 44, y: 61 },
  putrajaya: { x: 40, y: 69, side: "left" },
  "negeri sembilan": { x: 48, y: 74 },
  melaka: { x: 42, y: 82, side: "left" },
  johor: { x: 62, y: 88 },
};

const CITIES_BY_STATE = {
  kedah: ["Sungai Petani", "Alor Setar", "Kulim"],
  perak: ["Ipoh", "Taiping"],
  pahang: ["Kuantan", "Raub"],
  johor: ["Johor Bahru", "Muar", "Batu Pahat", "Kluang", "Pontian"],
  selangor: ["Shah Alam", "Klang"],
  melaka: ["Ayer Keroh", "Alor Gajah"],
  "negeri sembilan": ["Seremban", "Nilai"],
  terengganu: ["Kuala Terengganu", "Kemaman"],
  kelantan: ["Kota Bharu"],
  "pulau pinang": ["Georgetown", "Butterworth"],
  "kuala lumpur": ["KL Sentral"],
};

const MAP_IMG = "/map-malaysia.png";

export const Coverage = ({ coverage }) => {
  const { t } = usePrefs();
  return (
    <section data-testid="coverage-section" className="py-24 lg:py-32 bg-[#0b1533] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,88,12,0.08),transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fadeUp}>
          <span className="text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.coverage.badge}</span>
          <h2 className="font-heading font-bold text-white text-3xl lg:text-4xl mt-3 mb-6">{t.coverage.heading}</h2>
          <p className="text-slate-400 mb-10 max-w-md">{t.coverage.desc}</p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {coverage.map((c, i) => (
              <motion.div
                key={c}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`coverage-chip-${i}`}
                className="flex items-center gap-2.5 bg-slate-900/70 border border-white/10 rounded-lg px-4 py-3 hover:border-orange-500/50 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                <span className="text-slate-200 text-sm font-medium">{c}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="relative mx-auto w-full max-w-lg" data-testid="coverage-map">
          <div className="absolute -inset-6 bg-gradient-to-br from-orange-500/10 via-transparent to-orange-500/5 rounded-3xl blur-xl" />
          <div className="relative">
            <span className="absolute top-[3%] left-[42%] text-slate-500 text-[10px] tracking-widest font-semibold uppercase">Thailand</span>
            <span className="absolute top-[35%] right-[2%] text-slate-500 text-[10px] tracking-widest font-semibold uppercase leading-tight text-right">Laut China<br/>Selatan</span>
            <span className="absolute bottom-[1%] right-[24%] text-slate-500 text-[10px] tracking-widest font-semibold uppercase">Singapura</span>
            <img src={MAP_IMG} alt="Peta Semenanjung Malaysia" className="w-full h-auto opacity-90" />
            {coverage.map((c) => {
              const key = c.trim().toLowerCase();
              const pos = STATE_POS[key];
              if (!pos) return null;
              const cities = CITIES_BY_STATE[key] || [];
              return (
                <div
                  key={c}
                  className="absolute group z-10 hover:z-20"
                  style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -100%)" }}
                  data-testid={`map-pin-${key.replace(/\s+/g, "-")}`}
                >
                  <span className="absolute left-1/2 bottom-0 -translate-x-1/2 w-4 h-4 rounded-full bg-orange-500/60 map-pin-pulse" />
                  <MapPin size={26} className="relative text-orange-500 fill-orange-600/40 drop-shadow-[0_2px_8px_rgba(234,88,12,0.75)] transition-transform group-hover:scale-110" />
                  <div
                    className={`absolute top-0.5 whitespace-nowrap bg-slate-950/95 backdrop-blur-sm border border-orange-500/50 shadow-lg shadow-orange-500/10 rounded-lg px-3 py-1.5 transition-all group-hover:border-orange-400 group-hover:shadow-orange-500/30 ${
                      pos.side === "left" ? "right-7" : "left-7"
                    }`}
                  >
                    <span className="block text-white text-[11px] font-bold uppercase tracking-wide">{c}</span>
                    {cities.length > 0 && (
                      <ul className="mt-0.5 space-y-0.5 hidden group-hover:block">
                        {cities.slice(0, 5).map((city) => (
                          <li key={city} className="text-orange-300/90 text-[10px] leading-tight flex items-center gap-1">
                            <span className="w-0.5 h-0.5 rounded-full bg-orange-400" />
                            {city}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const HQ_IMG = "/hq-building.png";
const SIGNATURE_TRUCK_IMG = "/signature-truck.png";

export const SignatureTruck = () => {
  const { t } = usePrefs();
  return (
    <section data-testid="signature-truck-section" className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-[#0b1533] to-slate-950 py-20 lg:py-28">
      <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_30%,rgba(234,88,12,0.25),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(30,64,175,0.2),transparent_50%)]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 lg:mb-12"
        >
          <span className="inline-block text-orange-400 border border-orange-500/40 bg-orange-500/10 backdrop-blur rounded-full px-4 py-1.5 text-xs tracking-widest uppercase font-semibold mb-4">
            {t.signature.badge}
          </span>
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t.signature.heading}
          </h2>
          <p className="text-slate-300 mt-4 text-base lg:text-lg max-w-2xl mx-auto">{t.signature.desc}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -80, scale: 0.95 }}
          whileInView={{ opacity: 1, x: 0, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
          data-testid="signature-truck-image-wrap"
        >
          <div className="absolute -inset-4 lg:-inset-6 bg-gradient-to-r from-orange-500/20 via-transparent to-blue-500/20 blur-2xl rounded-3xl" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 ring-1 ring-white/10">
            <img
              src={SIGNATURE_TRUCK_IMG}
              alt="Tanjung Marina Global signature fleet truck"
              data-testid="signature-truck-image"
              className="w-full h-auto block"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent lg:from-transparent" />

            <div className="pointer-events-none absolute top-4 left-4 lg:top-6 lg:left-6 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/25 rounded-full px-3.5 py-1.5 text-white text-[10px] lg:text-xs font-semibold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              Live Fleet · Malaysia
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-8 lg:mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4"
          data-testid="signature-truck-pills"
        >
          {t.signature.pills.map((pill, i) => (
            <motion.div
              key={pill}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
              className="flex items-center gap-2.5 backdrop-blur bg-white/5 border border-white/15 rounded-full px-4 py-3 hover:bg-white/10 hover:border-orange-500/40 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
              <span className="text-white text-xs lg:text-sm font-semibold uppercase tracking-wide">{pill}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export const Clients = ({ clients }) => {
  const { t } = usePrefs();
  const list = Array.isArray(clients) ? clients.filter((c) => c && (c.name || c.logo)) : [];
  if (list.length === 0) return null;
  const initials = (name = "") =>
    name
      .replace(/[^\p{L}\s]/gu, " ")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 3);
  const track = [...list, ...list, ...list];
  return (
    <section data-testid="clients-section" className="py-20 lg:py-28 bg-white dark:bg-[#0b1533] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.clients.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.clients.heading}</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4">{t.clients.desc}</p>
        </motion.div>
      </div>

      <div className="marquee-container marquee-fade-mask overflow-hidden" data-testid="clients-marquee">
        <div className="marquee-track flex gap-6 w-max py-4">
          {track.map((client, i) => (
            <div
              key={`${client.name}-${i}`}
              data-testid={`client-card-${i}`}
              className="shrink-0 w-44 h-40 flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-900/60 px-4 py-4 hover:border-orange-500/60 hover:-translate-y-1 transition-all shadow-sm dark:shadow-none"
            >
              {client.logo ? (
                <img
                  src={fileUrl(client.logo)}
                  alt={client.name}
                  className="w-20 h-20 object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shadow-md shadow-orange-500/20">
                  <span className="text-white font-heading font-extrabold text-2xl tracking-wider">{initials(client.name) || "?"}</span>
                </div>
              )}
              <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 text-center leading-tight line-clamp-2">
                {client.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const HQShowcase = () => {
  const { t } = usePrefs();
  return (
    <section data-testid="hq-showcase-section" className="relative overflow-hidden bg-slate-950">
      <div className="relative">
        <motion.img
          src={HQ_IMG}
          alt="Ibu Pejabat Tanjung Marina Global"
          data-testid="hq-showcase-image"
          className="w-full h-auto block"
          initial={{ opacity: 0.4, scale: 1.02 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
        <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent pointer-events-none" />
        <div className="lg:absolute lg:inset-x-0 lg:bottom-0 bg-slate-950 lg:bg-transparent pt-10 lg:pt-0 pb-12 lg:pb-14">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div {...fadeUp}>
              <span className="inline-block text-orange-400 border border-orange-500/40 bg-orange-500/15 backdrop-blur rounded-full px-4 py-1.5 text-xs tracking-widest uppercase font-semibold mb-4">
                {t.hq.badge}
              </span>
              <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight mb-4">
                {t.hq.title}
              </h2>
              <p className="text-slate-300 max-w-xl mb-10">{t.hq.desc}</p>
            </motion.div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl">
              {t.hq.stats.map(([num, label], i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl px-5 py-4"
                  data-testid={`hq-stat-${i}`}
                >
                  <p className="font-heading font-extrabold text-orange-400 text-2xl lg:text-3xl">{num}</p>
                  <p className="text-slate-200 text-xs lg:text-sm mt-1">{label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ContactSection = ({ contact, services }) => {
  const { t } = usePrefs();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      trackConversion();
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-md px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

  return (
    <section id="hubungi" data-testid="contact-section" className="py-24 lg:py-32 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <motion.div {...fadeUp}>
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.contact.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3 mb-6">{t.contact.heading}</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-10">{t.contact.desc}</p>
          <div className="space-y-5">
            <div className="flex items-start gap-4 text-slate-700 dark:text-slate-300">
              <MapPin className="text-orange-600 dark:text-orange-500 shrink-0 mt-0.5" size={20} />
              <span data-testid="contact-address">{contact.address}</span>
            </div>
            <div className="flex items-center gap-4 text-slate-700 dark:text-slate-300">
              <Phone className="text-orange-600 dark:text-orange-500 shrink-0" size={20} />
              <a href={`tel:${contact.phone}`} className="hover:text-orange-600" data-testid="contact-phone">
                {contact.phone}
              </a>
            </div>
            <div className="flex items-center gap-4 text-slate-700 dark:text-slate-300">
              <Mail className="text-orange-600 dark:text-orange-500 shrink-0" size={20} />
              <span className="flex flex-col">
                <a href={`mailto:${contact.email}`} className="hover:text-orange-600" data-testid="contact-email">
                  {contact.email}
                </a>
                {contact.email2 && (
                  <a href={`mailto:${contact.email2}`} className="hover:text-orange-600" data-testid="contact-email2">
                    {contact.email2}
                  </a>
                )}
              </span>
            </div>
            <div className="flex items-center gap-4 text-slate-700 dark:text-slate-300">
              <Globe className="text-orange-600 dark:text-orange-500 shrink-0" size={20} />
              <span data-testid="contact-website">{contact.website}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          {...fadeUp}
          onSubmit={submit}
          data-testid="enquiry-form"
          className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-xl p-8 space-y-4 shadow-sm dark:shadow-none"
        >
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-xl mb-2">{t.contact.formTitle}</h3>
          <input
            data-testid="enquiry-name-input"
            required
            placeholder={t.contact.name}
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="enquiry-email-input"
              required
              type="email"
              placeholder={t.contact.email}
              className={inputCls}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              data-testid="enquiry-phone-input"
              required
              placeholder={t.contact.phone}
              className={inputCls}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <select
            data-testid="enquiry-service-select"
            className={inputCls}
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
          >
            <option value="">{t.contact.selectService}</option>
            {services.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
          <textarea
            data-testid="enquiry-message-input"
            required
            rows={4}
            placeholder={t.contact.message}
            className={inputCls}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-btn"
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-all"
          >
            {sending ? t.contact.sending : t.contact.send}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export const Footer = ({ contact, about }) => {
  const { t } = usePrefs();
  return (
    <footer data-testid="site-footer" className="bg-slate-100 border-t border-slate-200 pt-14 pb-6">
      <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <h4 className="font-heading font-bold text-[#0b1533] pb-3 mb-5 border-b border-slate-300">{t.footer.info}</h4>
          <img src={LOGO_URL} alt="Tanjung Marina Global" className="h-16 w-auto mb-4" />
          <p className="text-slate-600 text-sm leading-relaxed">
            {about?.text ? `${about.text.slice(0, 220)}${about.text.length > 220 ? "..." : ""}` : ""}
          </p>
          <p className="font-semibold text-[#0b1533] text-sm mt-5 mb-2">{t.footer.follow}</p>
          <a
            href={contact.facebook || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-facebook-link"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-300 bg-white text-slate-600 hover:text-white hover:bg-[#0b1533] transition-colors"
            aria-label="Facebook"
          >
            <Facebook size={16} />
          </a>
        </div>

        <div>
          <h4 className="font-heading font-bold text-[#0b1533] pb-3 mb-5 border-b border-slate-300">{t.footer.nav}</h4>
          <div className="space-y-3 text-sm">
            {[
              [t.footer.homeLink, "/"],
              [t.nav.about, "/tentang-kami"],
              [t.nav.team, "/pasukan-kami"],
              [t.nav.services, "/perkhidmatan"],
              [t.nav.gallery, "/galeri"],
              [t.nav.contact, "/hubungi"],
            ].map(([label, to]) => (
              <Link key={to} to={to} className="block text-slate-600 hover:text-orange-600 transition-colors">
                {label}
              </Link>
            ))}
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="block text-orange-600 font-semibold hover:text-orange-700 transition-colors"
            >
              {t.footer.adminAccess}
            </Link>
          </div>
        </div>

        <div>
          <h4 className="font-heading font-bold text-[#0b1533] pb-3 mb-5 border-b border-slate-300">{t.footer.contact}</h4>
          <div className="space-y-5 text-sm">
            <div>
              <p className="flex items-center gap-2 font-semibold text-[#0b1533] mb-1">
                <MapPin size={15} className="text-orange-600" /> {t.footer.loc}
              </p>
              <p className="text-slate-600" data-testid="footer-address">{contact.address}</p>
            </div>
            <div>
              <p className="flex items-center gap-2 font-semibold text-[#0b1533] mb-1">
                <Phone size={15} className="text-orange-600" /> {t.footer.phone}
              </p>
              <a href={`tel:${contact.phone}`} className="text-slate-600 hover:text-orange-600" data-testid="footer-phone">
                {contact.phone}
              </a>
            </div>
            <div>
              <p className="flex items-center gap-2 font-semibold text-[#0b1533] mb-1">
                <Mail size={15} className="text-orange-600" /> {t.footer.email}
              </p>
              <a href={`mailto:${contact.email}`} className="block text-slate-600 hover:text-orange-600" data-testid="footer-email">
                {contact.email}
              </a>
              {contact.email2 && (
                <a href={`mailto:${contact.email2}`} className="block text-slate-600 hover:text-orange-600" data-testid="footer-email2">
                  {contact.email2}
                </a>
              )}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading font-bold text-[#0b1533] pb-3 mb-5 border-b border-slate-300">{t.footer.location}</h4>
          <div className="relative rounded-lg overflow-hidden border border-slate-300">
            <iframe
              title="Lokasi Tanjung Marina Global"
              src={`https://www.google.com/maps?q=${encodeURIComponent(contact.address)}&output=embed`}
              className="w-full h-52"
              loading="lazy"
              data-testid="footer-map"
            />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-open-maps"
              className="absolute top-2 left-2 flex items-center gap-1.5 bg-white text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-md shadow hover:bg-blue-50 transition-colors"
            >
              Open in Maps <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-5 border-t border-slate-200 text-xs text-slate-500">
        © {new Date().getFullYear()} Tanjung Marina Global Sdn. Bhd. {t.footer.rights}
      </div>
    </footer>
  );
};
